import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Typography,
  Box,
  Paper,
  Checkbox,
  Avatar,
  LinearProgress,
  IconButton,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

const SeverityChips = ({ critical, medium, low }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box 
        sx={{
          backgroundColor: '#ef43431a',
          color: '#ef4343',
          border: '1px solid #ef434333',
          fontWeight: 600,
          fontSize: '0.65rem',
          height: 20,
          width: 30,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} 
      >
        {critical}
      </Box>
      <Box 
        sx={{
          backgroundColor: '#ff80521a',
          color: '#ff8052',
          border: '1px solid #ff805233',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 20,
          width: 30,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} 
      >
        {medium}
      </Box>
      <Box 
        sx={{
          backgroundColor: '#00bfff1a',
          color: '#00bfff',
          border: '1px solid #00bfff33',
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 20,
          width: 30,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} 
      >
        {low}
      </Box>
    </Box>
  );
};

const FixedIssuesProgress = ({ fixedData }) => {
  const { total, totalFixed, critical, medium, low, fixedCritical, fixedMedium, fixedLow, percentage } = fixedData;
  
  return (
    <Box sx={{ minWidth: 120 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {totalFixed} / {total}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#ff8052' }}>
          {percentage}%
        </Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={percentage} 
        sx={{ 
          height: 6, 
          borderRadius: 3, 
          backgroundColor: '#f5f5f580',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#1f1f1f',
            borderRadius: 3
          }
        }} 
      />
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
        <Chip 
          label={`${fixedCritical}/${critical}`} 
          size="small" 
          sx={{
            backgroundColor: '#ef43431a',
            color: '#ef4343',
            fontSize: '0.7rem',
            height: 18,
            borderRadius: 2
          }} 
        />
        <Chip 
          label={`${fixedMedium}/${medium}`} 
          size="small" 
          sx={{
            backgroundColor: '#ff80521a',
            color: '#ff8052',
            fontSize: '0.7rem',
            height: 18,
            borderRadius: 2
          }} 
        />
        <Chip 
          label={`${fixedLow}/${low}`} 
          size="small" 
          sx={{
            backgroundColor: '#00bfff1a',
            color: '#00bfff',
            fontSize: '0.7rem',
            height: 18,
            borderRadius: 2
          }} 
        />
      </Box>
    </Box>
  );
};

const StatusChip = ({ status }) => {
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Closed':
        return {
          backgroundColor: '#ef43431a',
          color: '#ef4343',
          border: '1px solid #ef434333'
        };
      case 'Merged':
        return {
          backgroundColor: '#f9fafb',
          color: '#374151',
          border: '1px solid #e5e7eb'
        };
      case 'Draft':
        return {
          backgroundColor: '#f5f5f580',
          color: '#737373',
          border: '1px solid #f5f5f5'
        };
      default:
        return {
          backgroundColor: '#00bfff1a',
          color: '#00bfff',
          border: '1px solid #00bfff33'
        };
    }
  };

  return (
    <Chip 
      label={status} 
      size="small" 
      sx={{
        ...getStatusStyle(status),
        fontSize: '0.75rem',
        height: 24,
        borderRadius: 2,
        fontWeight: 600
      }} 
    />
  );
};

const PRListTable = ({ 
  reviews, 
  columns, 
  sort, 
  onSort, 
  onRowClick,
  getIssuesCount,
  getSeverity,
  getFixedIssuesData,
  getStatus,
  selectedRows,
  onSelectAll,
  onSelectRow,
  loading = false
}) => {
  const isAllSelected = reviews.length > 0 && selectedRows.length === reviews.length;
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < reviews.length;

  return (
    <Paper 
      elevation={0}
      sx={{ 
        borderRadius: 1,
        border: '0 solid #fff',
        overflow: 'hidden'
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow 
              sx={{ 
                backgroundColor: '#fff',
                borderBottom: '2px solid #e5e7eb'
              }}
            >
              {columns.map((col) => (
                <TableCell 
                  key={col.id}
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: '#1f1f1f',
                    py: 2,
                    px: 3,
                    borderBottom: 'none'
                  }}
                >
                  {col.id === 'checkbox' ? (
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={onSelectAll}
                      sx={{ p: 0 }}
                    />
                  ) : col.sortable ? (
                    <TableSortLabel 
                      active={sort.field === col.id} 
                      direction={sort.field === col.id ? sort.direction : 'asc'} 
                      onClick={() => onSort(col.id)}
                      sx={{ 
                        fontWeight: 500,
                        '& .MuiTableSortLabel-icon': {
                          color: '#1f1f1f'
                        }
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Loading skeleton rows
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow 
                  key={`skeleton-${index}`}
                  sx={{ 
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.id} sx={{ py: 2, px: 3 }}>
                      {col.id === 'checkbox' ? (
                        <Skeleton variant="rectangular" width={20} height={20} />
                      ) : col.id === 'pr_author' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Skeleton variant="circular" width={24} height={24} />
                          <Skeleton variant="text" width={80} height={20} />
                        </Box>
                      ) : col.id === 'severity' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Skeleton variant="rectangular" width={30} height={20} />
                          <Skeleton variant="rectangular" width={30} height={20} />
                          <Skeleton variant="rectangular" width={30} height={20} />
                        </Box>
                      ) : col.id === 'fixed_issues' ? (
                        <Box sx={{ minWidth: 120 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Skeleton variant="text" width={40} height={20} />
                            <Skeleton variant="text" width={30} height={20} />
                          </Box>
                          <Skeleton variant="rectangular" width="100%" height={6} sx={{ borderRadius: 3 }} />
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                            <Skeleton variant="rectangular" width={40} height={18} sx={{ borderRadius: 2 }} />
                            <Skeleton variant="rectangular" width={40} height={18} sx={{ borderRadius: 2 }} />
                            <Skeleton variant="rectangular" width={40} height={18} sx={{ borderRadius: 2 }} />
                          </Box>
                        </Box>
                      ) : col.id === 'title' ? (
                        <Box>
                          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width="60%" height={16} />
                        </Box>
                      ) : col.id === 'status' ? (
                        <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 2 }} />
                      ) : col.id === 'actions' ? (
                        <Skeleton variant="circular" width={32} height={32} />
                      ) : (
                        <Skeleton variant="text" width={col.id === 'pr_number' ? 50 : 100} height={20} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : reviews.length === 0 ? (
              // Empty state
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="body1" sx={{ color: '#6b7280' }}>
                    No pull requests found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              reviews.map((pr, index) => {
              const fixedData = getFixedIssuesData(pr);
              const severityData = {
                critical: pr.critical_code_issues?.length || 0,
                medium: pr.medium_code_issues?.length || 0,
                low: pr.low_code_issues?.length || 0
              };
              
              return (
                <TableRow 
                  key={pr.unique_id} 
                  hover 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                    '&:hover': {
                      backgroundColor: '#f0f9ff',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                    }
                  }} 
                  onClick={() => onRowClick(pr)}
                >
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Checkbox
                      checked={selectedRows.includes(pr.unique_id)}
                      onChange={() => onSelectRow(pr.unique_id)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0 }}
                    />
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', color: '#1f1f1f', lineHeight: '1.25rem' }}>
                        {pr.repository}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3}}>
                    <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#1f1f1f' }}>
                      #{pr.pr_number}
                    </Typography>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3, maxWidth: 300 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', color: '#1f2937', mb: 0.5 }}>
                        {pr.title}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.4 }}>
                        {pr.title} {/* You can add description here */}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                        {pr.pr_author?.charAt(0) || 'U'}
                      </Avatar>
                      <Typography sx={{ fontSize: '0.9rem', color: '#374151' }}>
                        {pr.pr_author}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <SeverityChips {...severityData} />
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937' }}>
                        {getIssuesCount(pr)}
                      </Typography>
                      {getIssuesCount(pr) > 10 && (
                        <Box sx={{ width: 6, height: 6, backgroundColor: '#ef4343', borderRadius: '50%' }} />
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <FixedIssuesProgress fixedData={fixedData} />
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                        {format(new Date(pr.pr_created_at), 'MMM dd, yyyy')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {formatDistanceToNow(new Date(pr.pr_created_at), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    {pr.reviewed_at ? (
                      <Box>
                        <Typography sx={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>
                          {format(new Date(pr.reviewed_at), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {formatDistanceToNow(new Date(pr.reviewed_at), { addSuffix: true })}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
                        Not reviewed
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#737373' }}>
                        {pr.jira_accuracy_avg ? `${pr.jira_accuracy_avg.toFixed(0)}%` : 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <StatusChip status={getStatus(pr)} />
                  </TableCell>
                  
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <IconButton size="small" sx={{ p: 0.5 }} aria-label={`More options for PR #${pr.pr_number}`}>
                      <MoreVertIcon sx={{ fontSize: '1.2rem', color: '#6b7280' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PRListTable;